import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Calendar, Flag, Paperclip } from 'lucide-react';
import { Task } from '../../hooks/useTasks';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

interface TaskDrawerProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  orders: Array<{ id: string; title: string }>;
}

const TaskDrawer: React.FC<TaskDrawerProps> = ({
  task,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  orders,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo' as 'todo' | 'in_progress' | 'completed',
    priority: 'medium' as 'low' | 'medium' | 'high',
    order_id: '',
    due_date: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        order_id: task.order_id || '',
        due_date: task.due_date || '',
      });
      setIsEditing(false);
    }
  }, [task?.id, task?.status, task?.title, task?.description, task?.priority, task?.order_id, task?.due_date]);

  const handleSave = async () => {
    if (!task) return;

    try {
      await onUpdate(task.id, formData);
      setIsEditing(false);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await onDelete(task.id);
        onClose();
        toast.success('Task deleted successfully');
      } catch (error) {
        toast.error('Failed to delete task');
      }
    }
  };


  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          />

          {/* Modal Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
          >
            <div className="w-full max-w-2xl max-h-[90vh] theme-card-bg theme-card-border rounded-lg shadow-2xl flex flex-col overflow-hidden border"
                 onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div className="p-6 border-b theme-card-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:theme-hover-button rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 theme-text-secondary" />
                </button>
                <h2 className="text-xl font-semibold theme-text-primary">Task Details</h2>
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm theme-text-secondary hover:theme-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-blue-600 text-[rgb(var(--fg))] rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-[rgb(var(--fg))] rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 max-h-[calc(90vh-180px)]">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Title</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                ) : (
                  <h3 className="text-2xl font-bold theme-text-primary">{task.title}</h3>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Description</label>
                {isEditing ? (
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                  />
                ) : (
                  <p className="theme-text-secondary">{task.description || 'No description'}</p>
                )}
              </div>

              {/* Status - Always visible and editable */}
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value as 'todo' | 'in_progress' | 'completed';
                    // Update local state immediately for instant UI feedback
                    setFormData(prev => ({ ...prev, status: newStatus }));
                    // Auto-save on status change
                    if (task) {
                      try {
                        await onUpdate(task.id, { status: newStatus });
                        toast.success('Status updated');
                        // Force re-render by updating formData again (onUpdate should trigger task prop update from parent)
                        // If parent doesn't update, we've already set it above
                      } catch (error) {
                        toast.error('Failed to update status');
                        // Revert on error
                        setFormData(prev => ({ ...prev, status: task.status }));
                      }
                    }
                  }}
                  className={`w-full px-4 py-2.5 theme-input theme-text-primary rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all appearance-none ${
                    formData.status === 'todo' ? 'border-blue-500/30 bg-blue-500/5' :
                    formData.status === 'in_progress' ? 'border-yellow-500/30 bg-yellow-500/5' :
                    'border-green-500/30 bg-green-500/5'
                  }`}
                  style={{
                    backgroundColor: formData.status === 'todo' ? 'rgba(59, 130, 246, 0.05)' : 
                                   formData.status === 'in_progress' ? 'rgba(251, 191, 36, 0.05)' : 
                                   'rgba(34, 197, 94, 0.05)',
                  }}
                >
                  <option value="todo" className="theme-input theme-text-primary">To Do</option>
                  <option value="in_progress" className="theme-input theme-text-primary">In Progress</option>
                  <option value="completed" className="theme-input theme-text-primary">Completed</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Priority</label>
                {isEditing ? (
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20 capitalize">
                    <Flag className="w-4 h-4" />
                    {task.priority}
                  </span>
                )}
              </div>

              {/* Project and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">Project</label>
                  {isEditing ? (
                    <select
                      value={formData.order_id}
                      onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                      className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">No Project</option>
                      {orders.map(order => (
                        <option key={order.id} value={order.id}>{order.title}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="theme-text-secondary">
                      {task.order?.title || 'No project'}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">Due Date</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  ) : (
                    <span className="theme-text-secondary flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {task.due_date ? (() => {
                        // Éviter les problèmes de timezone lors du formatage
                        if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          const [year, month, day] = task.due_date.split('-').map(Number);
                          const date = new Date(year, month - 1, day);
                          return format(date, 'MMM dd, yyyy');
                        }
                        return format(new Date(task.due_date), 'MMM dd, yyyy');
                      })() : 'No due date'}
                    </span>
                  )}
                </div>
              </div>

              {/* Attachments Placeholder */}
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </label>
                <div className="p-4 theme-card-bg theme-card-border rounded-lg text-center border">
                  <p className="theme-text-tertiary text-sm">No attachments</p>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t theme-card-border flex items-center justify-between">
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Delete Task
              </button>
              <div className="text-xs theme-text-tertiary">
                Created {format(new Date(task.created_at), 'MMM dd, yyyy')}
              </div>
            </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TaskDrawer;

